import type { PublicFeedPhoto, PublicResident } from "@/shared/contracts";
import { publicIdentityBinding } from "../publicIdentityMedia";
import { nowLine, presenceGroup } from "../residentView";
import { eventText, type WorldFeedItem } from "./worldFeed";

/** Additive P114 fields until I regenerates shared contracts. No generated edits. */
export type ResidentLifePhoto = PublicFeedPhoto & { petId: string; mediaId: string; captured_at?: string | null; source_event_id?: string | null };
const timed = (value: unknown): value is string => typeof value === "string" && /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
/** Only P's native public-purpose URL; the URL subject, not participant order, owns this photo. */
export function publicStoryPhoto(photo: PublicFeedPhoto | null | undefined, event?: WorldFeedItem): ResidentLifePhoto | null {
  if (!photo || !event || event.kind !== "photo_posted" || typeof photo.url !== "string" || typeof photo.alt !== "string" || !photo.alt.trim()) return null;
  const match = /^\/api\/v1\/web\/public\/residents\/([A-Za-z0-9_-]+)\/life-photos\/([A-Za-z0-9_-]+)$/.exec(photo.url);
  if (!match || !event.pet_ids.includes(match[1])) return null;
  const extra = photo as PublicFeedPhoto & { captured_at?: unknown; source_event_id?: unknown };
  if (extra.captured_at != null && (!timed(extra.captured_at) || !timed(event.at) || Date.parse(extra.captured_at) > Date.parse(event.at))) return null;
  if (extra.source_event_id != null && (typeof extra.source_event_id !== "string" || !/^fe_[0-9a-f]{20}$/.test(extra.source_event_id) || extra.source_event_id === event.event_id)) return null;
  return { url: photo.url, alt: photo.alt, petId: match[1], mediaId: match[2], captured_at: (extra.captured_at as string | null | undefined) ?? null, source_event_id: (extra.source_event_id as string | null | undefined) ?? null };
}
export type IdentityFaceCrop = Readonly<{ x: number; y: number; width: number; height: number }>;
// P114 reviewed eight official-o1 references. Pixel ROI and source dimensions are
// layout metadata, not image URLs, authorization or a response SHA assertion.
const FACE_CROPS: Readonly<Record<string, readonly [number, number, number, number, number, number]>> = {
  "PJ-0B8B864F": [150, 750, 850, 850, 1664, 2496],
  "PJ-D570B44E": [680, 430, 810, 810, 1664, 2496],
  "PJ-83A4C91E": [740, 650, 800, 800, 1664, 2496],
  "PJ-F25EDEDB": [180, 200, 840, 840, 1664, 2496],
  "PJ-1BCA2894": [80, 150, 620, 620, 1024, 1536],
  "PJ-F26EE345": [300, 650, 1100, 1100, 1664, 2496],
  "PJ-22672A03": [340, 340, 650, 650, 1024, 1536],
  "PJ-5427BA15": [100, 70, 600, 600, 1024, 1536],
};
type IdentityCropSubject = Pick<PublicResident, "pet_id" | "identity" | "avatar_url" | "official_avatar">;
export function identityFaceCrop(resident: IdentityCropSubject): IdentityFaceCrop | null {
  const crop = FACE_CROPS[resident.pet_id];
  if (!crop || resident.identity?.version !== "o1" || !eligibleStoryResident(resident)) return null;
  return { x: crop[0] / crop[4], y: crop[1] / crop[5], width: crop[2] / crop[4], height: crop[3] / crop[5] };
}
export function eligibleStoryResident(resident: IdentityCropSubject): boolean {
  return Boolean(publicIdentityBinding({ petId: resident.pet_id, identity: resident.identity }, resident.avatar_url, resident.official_avatar === true));
}

/**
 * 「今天星球上发生的事」按故事排（UI-WORLD-LIVING-117-1，用户 10-15 经 I：有意思的在前，例行的合成一句）。
 * 只按事件种类、真实城市和 TA 自己写的公开小事分档，不从文字里猜：
 * - photo：P114 的具名生活照（publicStoryPhoto 过了才算，照片主人是谁就归谁）；
 * - met：两位以上居民真的碰见了（同一张卡里两位都算出场，不再各自另起一张）；
 * - event：一起出发去参加星球活动（event_joined）；
 * - trip：到了别的城市（arrived 的 area.city 和 TA 住的城市都写明、而且不一样）；
 * - out：此刻在外面或在路上（服务端 presence，不看 doing 文字），最近那件是例行的也提上来——「此刻」本身就是故事（2531 #4）；
 * - post：TA 自己写的、不挂在哪一趟出门上的公开小事（没有 visit_id）；
 * - routine：出门、到了同城某处、回家，以及挂在某一趟出门上的小事（有 visit_id，就是那一趟的随手记）——
 *   合成一句「另外 N 位最近出过门」，展开才一条条看；同一位有随手记就用 TA 自己的话。
 * 每位居民只出现一次，取最靠前的那一档；同一档里按事件流的先后（已接受的阅读顺序），小事按写的时间。
 * 带着照片的事件，照片对不上（退役明信片、私图、主体或来源不对）就整条当没有：不升级、不借形象照、不留没图的卡。
 * 页面上打不开的照片（badPhotos，图片读失败时面板记下的地址）同样整条当没有：卡片、把手那一句都不再说「分享了一张照片」（评审 R2 第 8 条）。
 * 什么都没有的居民在 quiet 里（在外面的照样提成 out）。
 */
export type DailyTier = "photo" | "met" | "event" | "trip" | "out" | "post" | "routine";
export const DAILY_TIERS: readonly DailyTier[] = ["photo", "met", "event", "trip", "out", "post", "routine"];
export type DailyStory = {
  key: string; tier: DailyTier;
  /** 出场的居民：一般一位；相遇是两位以上，第一位是这条卡片的主角 */
  residents: PublicResident[];
  text: string; at: string | null; photo: ResidentLifePhoto | null; area: string | null; event: WorldFeedItem | null;
};
export type DailyStories = { stories: DailyStory[]; routine: DailyStory[]; quiet: PublicResident[] };

export function dailyStories(residents: readonly PublicResident[], events: readonly WorldFeedItem[], accepted: readonly string[] | null, deniedIds: ReadonlySet<string> = new Set(), badPhotos: ReadonlySet<string> = new Set()): DailyStories {
  const valid = residents.filter(r => !deniedIds.has(r.pet_id) && eligibleStoryResident(r));
  const byId = new Map(valid.map(r => [r.pet_id, r]));
  const byEvent = new Map(events.map(e => [e.event_id, e]));
  const order = (accepted ?? events.map(e => e.event_id)).filter(id => byEvent.has(id));
  const nameOf = (p: string) => byId.get(p)?.name ?? null;
  const candidates: (DailyStory & { rank: number })[] = [];
  order.forEach((id, index) => {
    const event = byEvent.get(id)!;
    const pets = [...new Set(event.pet_ids)];
    if (!pets.length || pets.some(p => !byId.has(p))) return;
    const base = { key: event.event_id, text: eventText(event, nameOf), at: event.at, area: event.area?.name?.trim() || null, event, rank: index };
    // 带着照片的事件：照片过不了 P114 的公开读口规矩（退役明信片、私图、主体或来源对不上），整条不要——不升级、不换成形象照、也不留一张没图的卡（失败就关）
    if (event.photo != null || event.kind === "photo_posted") {
      let photo = publicStoryPhoto(event.photo, event);
      const origin = photo?.source_event_id ? byEvent.get(photo.source_event_id) : undefined;
      if (origin && (origin.kind !== "arrived" || !origin.pet_ids.includes(photo!.petId))) photo = null;
      if (!photo || badPhotos.has(photo.url)) return;
      candidates.push({ ...base, tier: "photo", residents: [byId.get(photo.petId)!, ...pets.filter(p => p !== photo!.petId).map(p => byId.get(p)!)], photo });
      return;
    }
    const lead = byId.get(pets[0])!;
    if (event.kind === "met") {
      if (pets.length >= 2) candidates.push({ ...base, tier: "met", residents: pets.map(p => byId.get(p)!), photo: null });
      return;
    }
    if (event.kind === "event_joined") { candidates.push({ ...base, tier: "event", residents: [lead], photo: null }); return; }
    const home = lead.city?.trim(), there = event.area?.city?.trim();
    const tier: DailyTier = event.kind === "arrived" && home && there && home !== there ? "trip" : "routine";
    candidates.push({ ...base, tier, residents: [lead], photo: null });
  });
  // TA 自己写的公开小事：不挂在出门上的自成一档；挂在某一趟出门上的（visit_id）算那一趟的随手记，归例行。
  // 档里新写的在前（时间读不出来的排最后）；排在同档事件前面，同一位有随手记就用 TA 自己的话
  const posts = valid.flatMap(r => {
    const post = r.recent_posts.find(p => p.visibility === "public" && p.author?.actor_kind === "pet" && p.author.actor_id === r.pet_id);
    if (!post) return [];
    const written = Date.parse(post.created_at);
    return [{ key: `post:${r.pet_id}:${post.post_id ?? post.created_at}`, tier: (post.visit_id ? "routine" : "post") as DailyTier, residents: [r], text: post.text, at: post.created_at, photo: null, area: r.city ? `${r.city}居民` : null, event: null,
      rank: Number.isFinite(written) ? -written : Number.MAX_SAFE_INTEGER }];
  });
  const all = [...candidates, ...posts].sort((a, b) => DAILY_TIERS.indexOf(a.tier) - DAILY_TIERS.indexOf(b.tier) || a.rank - b.rank);
  const seen = new Set<string>();
  const picked: (DailyStory & { rank: number })[] = [];
  for (const story of all) {
    // 主角已经出过场的不再出；相遇那张只要有一位出过场就不再出，免得同一位出现两次
    if (story.residents.some(r => seen.has(r.pet_id))) continue;
    story.residents.forEach(r => seen.add(r.pet_id));
    picked.push(story);
  }
  // 此刻在外面（服务端 presence）：例行的、TA 自己的小事都提成 out；什么都没有的在外面居民也提上来，只说此刻
  const away = (r: PublicResident) => { const g = presenceGroup(r.presence); return g === "out" || g === "road"; };
  for (const story of picked) if ((story.tier === "routine" || story.tier === "post") && away(story.residents[0])) story.tier = "out";
  const quiet: PublicResident[] = [];
  for (const r of valid) {
    if (seen.has(r.pet_id)) continue;
    if (away(r)) picked.push({ key: `resident:${r.pet_id}`, tier: "out", residents: [r], text: nowLine(r), at: null, photo: null, area: r.city ? `${r.city}居民` : null, event: null, rank: Number.MAX_SAFE_INTEGER });
    else quiet.push(r);
  }
  picked.sort((a, b) => DAILY_TIERS.indexOf(a.tier) - DAILY_TIERS.indexOf(b.tier) || a.rank - b.rank);
  const strip = ({ rank: _rank, ...story }: DailyStory & { rank: number }): DailyStory => story;
  return { stories: picked.filter(s => s.tier !== "routine").map(strip), routine: picked.filter(s => s.tier === "routine").map(strip), quiet };
}

/**
 * 只看这一层的那几位（UI-WORLD-LIVING-117-2，I 10-15：在哪一层说哪一层的今天——星球层说星球，城市层说这座城）：
 * 故事里有一位在这一层就留（相遇的另一位在别处也照样说），例行的、什么都没有的按 TA 本人；不在这一层的不说。排序照旧。
 */
export function scopeDaily(daily: DailyStories, ids: ReadonlySet<string>): DailyStories {
  return {
    stories: daily.stories.filter(story => story.residents.some(r => ids.has(r.pet_id))),
    routine: daily.routine.filter(story => ids.has(story.residents[0].pet_id)),
    quiet: daily.quiet.filter(r => ids.has(r.pet_id)),
  };
}

/** 一位居民在故事排序里最靠前的那一条（就近面板说「最近一件事」、找 TA 的生活照用）；故事卡在前，例行的在后；没有为 null */
export function storyOf(daily: DailyStories, petId: string): DailyStory | null {
  return daily.stories.find(story => story.residents.some(r => r.pet_id === petId)) ?? daily.routine.find(story => story.residents[0].pet_id === petId) ?? null;
}
